<template>
  <!--
    AB#21539 — the Files tab in the order pane's vocabulary. Factory tabs with
    counts, one toolbar (search · list/grid · New folder · Upload), a
    breadcrumb that says what the folder holds, rows or tiles with the facts
    (contents or size, modified, by), a ⋯ on every item carrying the actions
    the right-click menu had, and a preview pane for the file in hand. The
    whole card is the drop target. Every behaviour the tab had is still
    wired to the same handlers: factory switch, breadcrumbs, upload with
    progress and cancel, new folder, cut / copy / paste with conflicts,
    rename, delete, activity logs, download, multi-select, drag to move.
  -->
  <div class="of" data-test="order-files">
    <div class="of-card" :class="{ 'of-card--drop': isDragging && !isMovingItems }" @dragover.prevent="isDragging = true" @dragleave.prevent="handleContainerDragLeave">
      <!-- toolbar -->
      <div class="of-top">
        <div class="of-tabs" role="tablist" aria-label="Factory">
          <button
            v-for="(orderFactory, orderFactoryIndex) in orderFactories"
            :key="`${orderFactory.id}_${orderFactoryIndex}`"
            type="button"
            role="tab"
            class="of-tab"
            :class="{ 'of-tab--on': activeFactory.id === orderFactory.id }"
            :aria-selected="activeFactory.id === orderFactory.id"
            :data-test="`of-tab-${orderFactory.id}`"
            @click="handleFactoryUpdate(orderFactory)"
          >
            {{ orderFactory.name }}
            <span v-if="activeFactory.id === orderFactory.id && !isLoading" class="of-tab-n">{{ driveItems?.length ?? 0 }}</span>
          </button>
        </div>
        <span class="of-sp"></span>
        <template v-if="!sharePointDbSettingsNotFound">
          <label class="of-search">
            <MagnifyingGlassIcon class="size-4" aria-hidden="true" />
            <input v-model="query" type="search" placeholder="Search this folder and subfolders" aria-label="Search this folder and its subfolders" data-test="of-search" />
          </label>
          <span class="of-seg" role="group" aria-label="View">
            <button type="button" :class="{ 'of-seg--on': view === 'list' }" title="List" aria-label="List view" :aria-pressed="view === 'list'" data-test="of-view-list" @click="setView('list')">
              <Bars3Icon class="size-4" />
            </button>
            <button type="button" :class="{ 'of-seg--on': view === 'grid' }" title="Grid" aria-label="Grid view" :aria-pressed="view === 'grid'" data-test="of-view-grid" @click="setView('grid')">
              <Squares2X2Icon class="size-4" />
            </button>
          </span>
          <button type="button" class="dash-btn" data-test="of-new-folder" @click="openCreateFolderModal">New folder</button>
          <button type="button" class="dash-btn dash-btn--primary" data-test="of-upload" @click="fileInput?.click()">
            <ArrowUpTrayIcon class="size-4" />
            Upload
          </button>
        </template>
      </div>

      <div v-if="sharePointDbSettingsNotFound" class="of-blocked" data-test="of-not-configured">
        Unable to access the SharePoint drive because the necessary SharePoint factory settings are not configured.
      </div>

      <template v-else>
        <!-- breadcrumb -->
        <nav v-if="driveBreadCrumbs?.length" class="of-crumb" aria-label="Folder path" data-test="of-crumb">
          <FolderIcon class="size-4 of-crumb-ico" aria-hidden="true" />
          <template v-for="(breadcrumb, index) in driveBreadCrumbs" :key="breadcrumb.name + '_' + index">
            <span v-if="index > 0" class="of-crumb-sep">/</span>
            <b v-if="breadcrumb.isActive" class="of-crumb-here">{{ breadcrumb.name === '/' ? 'Home' : breadcrumb.name }}</b>
            <button v-else type="button" class="of-crumb-a" @click="getSharePointFilesAtPath(breadcrumb.path)">
              {{ breadcrumb.name === '/' ? 'Home' : breadcrumb.name }}
            </button>
          </template>
          <span class="of-crumb-meta" data-test="of-crumb-meta">{{ query ? searchSummary : folderSummary }}</span>
          <button v-if="itemActionInfo && ['cut', 'copy'].includes(itemActionInfo.action)" type="button" class="of-paste" data-test="of-paste-here" @click="handleDriveItemAction('paste')">
            <ClipboardDocumentIcon class="size-4" />
            Paste here
          </button>
        </nav>

        <div class="of-split" :class="{ 'of-split--preview': previewItem }">
          <!-- the folder -->
          <div
            class="of-body"
            ref="fileContainer"
            @mousedown="handleMouseDown"
            @mousemove="updateSelection"
            @contextmenu.prevent="handleContainerContextMenu($event)"
            @dragover.prevent="handleContainerDragOver"
            @dragleave.prevent="handleContainerDragLeave"
            @drop.prevent="handleContainerDrop"
          >
            <div v-if="isLoading || isOperationLoading" class="of-loading" data-test="of-loading">
              <span class="of-spinner" aria-hidden="true"></span>
            </div>

            <!-- list -->
            <table v-if="view === 'list'" class="of-table" data-test="of-list">
              <thead>
                <tr>
                  <th><button type="button" class="of-sort" :aria-sort="sortKey === 'name' ? sortDir : 'none'" @click="sortBy('name')">Name <ArrowsUpDownIcon class="size-3" /></button></th>
                  <th class="of-col-n">Contents</th>
                  <th class="of-col-d"><button type="button" class="of-sort" :aria-sort="sortKey === 'modified' ? sortDir : 'none'" @click="sortBy('modified')">Modified <ArrowsUpDownIcon class="size-3" /></button></th>
                  <th class="of-col-by">By</th>
                  <th class="of-col-x"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!visibleItems.length" class="of-empty-row">
                  <td colspan="5">
                    <span v-if="query && searching">Searching {{ currentFolderName }} and its subfolders…</span>
<span v-else-if="query">Nothing in {{ currentFolderName }} or its subfolders matches “{{ query }}”.</span>
                    <span v-else>This folder is empty — drop files here or use Upload.</span>
                  </td>
                </tr>
                <tr
                  v-for="driveItem in visibleItems"
                  :key="driveItem.id"
                  class="of-row drive-item file-item"
                  :class="{
                    'of-row--sel': selectedFiles.includes(driveItem),
                    'of-row--over': dragOverItem === driveItem && driveItem.type === 'folder',
                  }"
                  :data-item-id="driveItem.id"
                  :data-test="`of-item-${driveItem.id}`"
                  draggable="true"
                  :title="driveItem.type === 'file' ? `${driveItem.name} · ${sizeText(driveItem)}` : driveItem.name"
                  @click="handleItemClick($event, driveItem)"
                  @dblclick="handleItemDoubleClick(driveItem)"
                  @contextmenu.prevent="handleContextMenu($event, driveItem)"
                  @dragstart="handleDragStart($event, driveItem)"
                  @dragover.prevent="handleDragOver($event, driveItem)"
                  @dragleave.prevent="handleDragLeave($event, driveItem)"
                  @drop.prevent="handleDrop($event, driveItem)"
                  @dragend="handleDragEnd"
                >
                  <td>
                    <span class="of-name">
                      <FolderIcon v-if="driveItem.type === 'folder'" class="size-[18px] of-ico-folder" aria-hidden="true" />
                      <img v-else-if="thumbUrl(driveItem)" :src="thumbUrl(driveItem)" class="of-ico-thumb" alt="" />
                      <DocumentIcon v-else class="size-[18px] of-ico-file" aria-hidden="true" />
                      <span class="of-name-t">{{ baseName(driveItem) }}</span>
                      <span v-if="driveItem.type === 'file' && driveItem.extension" class="of-ext">.{{ driveItem.extension }}</span>
                      <button v-if="query && !isInOpenFolder(driveItem)" type="button" class="of-where" :title="`Open ${folderLabel(driveItem.parent_info?.path)}`" :data-test="`of-where-${driveItem.id}`" @click.stop="getSharePointFilesAtPath(driveItem.parent_info.path)" @dblclick.stop>
                        in {{ folderLabel(driveItem.parent_info?.path) }}
                      </button>
                    </span>
                  </td>
                  <td class="of-col-n of-mono">{{ contentsText(driveItem) }}</td>
                  <td class="of-col-d of-mono of-muted">{{ dateText(driveItem.modified_at) }}</td>
                  <td class="of-col-by">
                    <span v-if="driveItem.modified_by" class="of-by"><span class="of-av" :style="{ background: avatarColor(editorName(driveItem.modified_by)) }">{{ initials(editorName(driveItem.modified_by)) }}</span>{{ firstName(editorName(driveItem.modified_by)) }}</span>
                    <span v-else class="of-muted">—</span>
                  </td>
                  <td class="of-col-x" @click.stop @dblclick.stop>
                    <button type="button" class="of-kebab" :title="`Actions for ${driveItem.name}`" :aria-label="`Actions for ${driveItem.name}`" :data-test="`of-menu-${driveItem.id}`" @click="handleContextMenu($event, driveItem)">
                      <EllipsisVerticalIcon class="size-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- grid -->
            <div v-else class="of-grid" data-test="of-grid">
              <p v-if="!visibleItems.length" class="of-empty">
                <span v-if="query && searching">Searching {{ currentFolderName }} and its subfolders…</span>
<span v-else-if="query">Nothing in {{ currentFolderName }} or its subfolders matches “{{ query }}”.</span>
                <span v-else>This folder is empty — drop files here or use Upload.</span>
              </p>
              <div
                v-for="driveItem in visibleItems"
                :key="driveItem.id"
                class="of-tile drive-item file-item"
                :class="{
                  'of-tile--sel': selectedFiles.includes(driveItem),
                  'of-tile--over': dragOverItem === driveItem && driveItem.type === 'folder',
                }"
                :data-item-id="driveItem.id"
                :data-test="`of-item-${driveItem.id}`"
                draggable="true"
                :title="driveItem.type === 'file' ? `${driveItem.name} · ${sizeText(driveItem)}` : driveItem.name"
                @click="handleItemClick($event, driveItem)"
                @dblclick="handleItemDoubleClick(driveItem)"
                @contextmenu.prevent="handleContextMenu($event, driveItem)"
                @dragstart="handleDragStart($event, driveItem)"
                @dragover.prevent="handleDragOver($event, driveItem)"
                @dragleave.prevent="handleDragLeave($event, driveItem)"
                @drop.prevent="handleDrop($event, driveItem)"
                @dragend="handleDragEnd"
              >
                <div class="of-thumb">
                  <FolderIcon v-if="driveItem.type === 'folder'" class="size-11 of-ico-folder" aria-hidden="true" />
                  <img v-else-if="thumbUrl(driveItem)" :src="thumbUrl(driveItem)" class="of-thumb-img" alt="" />
                  <DocumentIcon v-else class="size-11 of-ico-file" aria-hidden="true" />
                </div>
                <div class="of-tile-t">{{ driveItem.name }}</div>
                <div class="of-tile-s"><span>{{ contentsText(driveItem) }}</span><span>{{ driveItem.modified_by ? firstName(editorName(driveItem.modified_by)) : dateText(driveItem.modified_at) }}</span></div>
                <div v-if="query && !isInOpenFolder(driveItem)" class="of-tile-where">in {{ folderLabel(driveItem.parent_info?.path) }}</div>
                <button type="button" class="of-kebab of-tile-kebab" :title="`Actions for ${driveItem.name}`" :aria-label="`Actions for ${driveItem.name}`" :data-test="`of-menu-${driveItem.id}`" @click.stop="handleContextMenu($event, driveItem)" @dblclick.stop>
                  <EllipsisVerticalIcon class="size-4" />
                </button>
              </div>
            </div>

            <div v-if="isSelecting" class="of-marquee" :style="{ left: selectionBox.left + 'px', top: selectionBox.top + 'px', width: selectionBox.width + 'px', height: selectionBox.height + 'px' }"></div>
          </div>

          <!-- preview -->
          <aside v-if="previewItem" class="of-preview" data-test="of-preview" aria-label="File preview">
            <button type="button" class="dash-icon-btn of-preview-x" title="Close preview" aria-label="Close preview" data-test="of-preview-close" @click="selectedFiles = []">
              <XMarkIcon class="size-5" />
            </button>
            <div class="of-preview-big" :class="{ 'of-preview-big--none': !thumbUrl(previewItem) }">
              <img v-if="thumbUrl(previewItem)" :src="thumbUrl(previewItem)" :alt="previewItem.name" data-test="of-preview-img" />
              <template v-else-if="thumbPending(previewItem)">
                <span class="of-spinner" aria-hidden="true"></span>
                <span class="of-preview-note" data-test="of-preview-pending">Preparing preview…</span>
              </template>
              <template v-else>
                <span class="of-ext-badge">{{ (previewItem.extension || 'file').toUpperCase() }}</span>
                <span class="of-preview-note" data-test="of-preview-none">No preview for this file type</span>
              </template>
            </div>
            <h4 class="of-preview-t">{{ previewItem.name }}</h4>
            <dl class="of-preview-dl">
              <dt>Size</dt><dd>{{ sizeText(previewItem) }}</dd>
              <dt>Modified</dt><dd>{{ dateText(previewItem.modified_at, true) || '—' }}</dd>
              <dt>By</dt><dd>{{ editorName(previewItem.modified_by) || '—' }}</dd>
              <dt>In</dt><dd>{{ folderLabel(previewItem.parent_info?.path) }}</dd>
            </dl>
            <div class="of-preview-acts">
              <a v-if="previewItem.download_url" :href="previewItem.download_url" download class="dash-btn dash-btn--primary" data-test="of-preview-download">Download</a>
              <button type="button" class="dash-btn" data-test="of-preview-rename" @click="renameFromPreview(previewItem)">Rename</button>
              <button type="button" class="dash-btn" data-test="of-preview-logs" @click="showFileLogs(previewItem)">Activity</button>
              <button type="button" class="dash-btn of-danger" data-test="of-preview-delete" @click="deleteFromPreview(previewItem)">Delete</button>
            </div>
          </aside>
        </div>

        <div class="of-foot" data-test="of-foot">
          <span>{{ footText }}</span>
          <span class="of-muted">Shift / Ctrl to select many · drag onto a folder to move · right-click for actions</span>
        </div>
      </template>

      <div v-if="isDragging && !isMovingItems" class="of-dropcue" aria-hidden="true">Drop to upload into {{ currentFolderName }}</div>

      <!-- Context menu: the same actions, reachable from ⋯ and from right-click -->
      <div v-if="contextMenu.visible" tabindex="-1" ref="contextMenuRef" :style="contextMenu.style" class="of-menu" role="menu" data-test="of-context-menu">
        <button v-if="contextMenu.item && !Array.isArray(contextMenu.item)" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-open" @click="handleItemDoubleClick(contextMenu.item)">
          {{ contextMenu.item.type === 'folder' ? 'Open' : 'Download' }}
        </button>
        <button type="button" role="menuitem" class="of-menu-i" @click="refreshDirectory">Refresh</button>
        <hr v-if="contextMenu.item" class="of-menu-hr" />
        <button v-if="contextMenu.item && !Array.isArray(contextMenu.item)" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-rename" @click="openRenameModal">Rename</button>
        <button v-if="contextMenu.item" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-cut" @click="handleDriveItemAction('cut')">Cut</button>
        <button v-if="contextMenu.item" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-copy" @click="handleDriveItemAction('copy')">Copy</button>
        <button v-if="itemActionInfo && ['cut', 'copy'].includes(itemActionInfo.action)" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-paste" @click="handleDriveItemAction('paste')">
          Paste {{ contextMenu.item && !Array.isArray(contextMenu.item) && contextMenu.item.type === 'folder' ? 'into' : 'here' }}
        </button>
        <hr v-if="contextMenu.item" class="of-menu-hr" />
        <button v-if="contextMenu.item" type="button" role="menuitem" class="of-menu-i" data-test="of-menu-logs" @click="showFileLogs(contextMenu.item)">Activity log</button>
        <button v-if="contextMenu.item" type="button" role="menuitem" class="of-menu-i of-menu-i--danger" data-test="of-menu-delete" @click="showDeleteConfirmModal">Delete</button>
      </div>

      <!-- File Input (hidden) -->
      <input ref="fileInput" type="file" multiple class="hidden" @change="handleSharePointFilesChangeEvent" />

      <!-- Modals -->
      <!-- Create Folder Modal -->
      <Transition name="fade">
        <Dialog
          v-if="showCreateFolderModal"
          title="Create New Folder"
          :showCloseButton="true"
          :showFooter="true"
          :showSaveButton="true"
          :showCancelButton="true"
          :sm="true"
          @closeEvent="
            () => {
              newFolderName = '';
              showCreateFolderModal = false;
            }
          "
          @cancelEvent="
            () => {
              newFolderName = '';
              showCreateFolderModal = false;
            }
          "
          @saveEvent="handleDriveItemAction('new_folder')"
          :saveButtonProps="{
            disabled: !newFolderName,
          }"
        >
          <template #modal-content>
            <input
              v-model="newFolderName"
              placeholder="Enter folder name"
              class="w-full border rounded p-2 mt-2"
            />
          </template>
        </Dialog>
        <!-- <div class="bg-surface rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Create New Folder</h3>

          <div class="flex justify-end gap-2">
            <button
              @click="showCreateFolderModal = false"
              class="px-4 py-2 border rounded hover:bg-surface-2"
            >
              Cancel
            </button>
            <button
              @click="handleDriveItemAction('new_folder')"
              class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
            >
              Create
            </button>
          </div>
        </div>
      </div> -->
      </Transition>

      <!-- Confirmation Dialog -->
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[100030]"
      >
        <div class="bg-surface rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Confirm Delete</h3>
          <p v-if="Array.isArray(contextMenu.item) && contextMenu.item.length > 1">
            Are you sure you want to delete these
            {{ contextMenu.item.length }} items?
          </p>
          <p v-else>Are you sure you want to delete this item?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="showDeleteConfirm = false"
              class="px-4 py-2 border rounded hover:bg-surface-2"
              :disabled="isOperationLoading"
            >
              Cancel
            </button>
            <button
              @click="handleDriveItemAction('delete')"
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              :disabled="isOperationLoading"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Upload Cancel Confirm -->
      <div
        v-if="showUploadCancelConfirm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-surface rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Upload in progress!</h3>
          <p>Current uploading files will be cancelled! Are you sure you want to leave site?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="showUploadCancelConfirm = false"
              class="px-4 py-2 border rounded hover:bg-surface-2"
            >
              Cancel
            </button>
            <button
              @click="handleUploadCancelConfirm(true)"
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      <!-- Rename Dialog -->
      <div
        v-if="showRenameModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-surface rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Rename Item</h3>
          <input
            v-model="driveItemNewName"
            placeholder="Enter new file name"
            class="w-full border rounded p-2 mb-4"
          />
          <div class="flex justify-end gap-2">
            <button
              @click="showRenameModal = false"
              class="px-4 py-2 border rounded hover:bg-surface-2"
              :disabled="isOperationLoading"
            >
              Cancel
            </button>
            <button
              @click="renameItem"
              class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
              :disabled="isOperationLoading"
            >
              Rename
            </button>
          </div>
        </div>
      </div>

      <!-- Conflict Modal -->
      <div
        v-if="showConflictModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-surface rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">File Conflict Detected</h3>
          <p>One or more files with the same name already exist in the destination folder.</p>
          <p>Would you like to rename the files automatically to avoid conflicts?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="resolveConflict(false)"
              class="px-4 py-2 border rounded hover:bg-surface-2"
            >
              Cancel Operation
            </button>
            <button
              @click="resolveConflict(true)"
              class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
            >
              Rename Files
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress -->
    <div
      v-if="Object.keys(uploadProgress).length > 0 || preparingUploads.length > 0"
      class="fixed bottom-5 right-5 w-[300px] max-h-[200px] overflow-y-auto bg-surface border border-line rounded p-2.5 shadow-md"
    >
      <div class="flex justify-between pb-2">
        <h5 class="text-base font-medium">Upload Progress:</h5>
      </div>
      <div v-for="(progress, fileName) in uploadProgress" :key="fileName" class="mb-2">
        <UploadProgressBar
          :progress="progress"
          :file-name="String(fileName)"
          :preparing="uploadPreparation[fileName]"
        />
        <button
          @click="cancelUpload(String(fileName))"
          class="ml-2.5 bg-red-500 text-white border-none rounded px-2 py-0.5 hover:bg-red-400 float-right"
        >
          Cancel
        </button>
      </div>
      <div v-for="item in preparingUploads" :key="item.fileName" class="mb-2">
        <UploadProgressBar :progress="0" :file-name="String(item.fileName)" :preparing="true" />
        <button
          @click="cancelUpload(String(item.fileName))"
          class="ml-2.5 bg-red-500 text-white border-none rounded px-2 py-0.5 hover:bg-red-400 float-right"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Multi-file Upload Progress -->
    <div
      v-if="multiFileUpload.inProgress"
      class="fixed bottom-[230px] right-5 w-[300px] bg-surface border border-line rounded p-2.5 shadow-md z-10"
    >
      <h5 class="text-base font-medium">
        Files {{ multiFileUpload.completed }} of {{ multiFileUpload.total }} uploading
      </h5>
      <div class="w-full h-5 bg-surface-2 rounded-full overflow-hidden mt-1.5">
        <div
          class="h-full bg-primary transition-all duration-300"
          :style="{
            width: `${(multiFileUpload.completed / multiFileUpload.total) * 100}%`,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Dialog from '@/components/Shared/Dialog.vue';
import { messageForError } from '@/utils/userMessage';
import { showToast } from '@/composables/swal';
import { useSharepointFiles } from '@/composables/useSharepointFiles';
import type { DriveItem, Factory, FactoryId } from '@/types/sharepoint';
import {
  avatarColor,
  baseName,
  contentsText,
  dateText,
  editorName,
  filterDriveItems,
  firstName,
  folderLabel,
  folderSummaryText,
  mergeSearchResults,
  samePath,
  footText as footTextFor,
  initials,
  readFilesView,
  sizeText,
  sortDriveItems,
  writeFilesView,
  type FilesSortDir,
  type FilesSortKey,
  type FilesView,
} from '@/utils/orderFiles';
import {
  ArrowUpTrayIcon,
  ArrowsUpDownIcon,
  Bars3Icon,
  ClipboardDocumentIcon,
  DocumentIcon,
  EllipsisVerticalIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import UploadProgressBar from './components/UploadProgressBar.vue';

// Props
const props = defineProps<{
  order?: any;
  orderId: FactoryId;
  orderFactories: Factory[];
}>();

//Events Emits
const emits = defineEmits([
  'show-logs',
  'loading-logs',
]);

// Refs
const fileInput = ref<HTMLInputElement | null>(null);
const fileContainer = ref<HTMLDivElement | null>(null);
const isDragging = ref(false);

// Use the composable
const {
  isLoading,
  isOperationLoading,
  driveItems,
  driveItemsParentInfo,
  driveBreadCrumbs,
  activeFactory,
  sharePointDbSettingsNotFound,
  contextMenu,
  contextMenuRef,
  selectedFiles,
  isSelecting,
  selectionBox,
  uploadProgress,
  uploadPreparation,
  multiFileUpload,
  isUploading,
  itemActionInfo,
  moveOperationInProgress,
  isMovingItems,
  newFolderName,
  driveItemNewName,
  showCreateFolderModal,
  showDeleteConfirm,
  showRenameModal,
  fetchSharePointFiles,
  fetchFactorySharepointInfo,
  handleDriveItemAction,
  uploadSharepointFile,
  uploadLargeSharepointFile,
  clearFileUploadProgress,
  setMultiFileUpload,
  resetMultiFileUpload,
  getFileThumbnails,
  thumbnails,
  moveSharepointItem,
  cancelTokens,
  removeCancelToken,
  setUploadPreparation,
  clearUploadPreparation,
  getFileLogs,
  searchSharepointFiles,
  getServerPreview
} = useSharepointFiles(props.orderId, props.orderFactories);

// Computed properties
const preparingUploads = computed(() => {
  return Object.entries(uploadPreparation.value)
    .filter(([fileName, preparing]) => preparing && !uploadProgress.value[fileName])
    .map(([fileName]) => ({ fileName }));
});

// AB#21539 — view state: search, list/grid, sort, preview
const query = ref('');
const view = ref<FilesView>(readFilesView(typeof window === 'undefined' ? null : window.localStorage));
const sortKey = ref<FilesSortKey>('name');
const sortDir = ref<FilesSortDir>('ascending');

const setView = (next: FilesView) => {
  view.value = next;
  writeFilesView(typeof window === 'undefined' ? null : window.localStorage, next);
};

const sortBy = (key: FilesSortKey) => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'ascending' ? 'descending' : 'ascending';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'modified' ? 'descending' : 'ascending';
  }
};

// Search covers the open folder and everything under it: the folder's own items
// match instantly on the client, and the server's subtree search (Graph, debounced)
// is merged in as it answers. The union is de-duplicated by id.
const searchResults = ref<DriveItem[]>([]);
const searching = ref(false);
let searchTimer: number | null = null;
let searchSeq = 0;

const runSearch = async (q: string) => {
  const seq = ++searchSeq;
  searching.value = true;
  try {
    const found = await searchSharepointFiles(q);
    if (seq === searchSeq) searchResults.value = found;
  } catch {
    if (seq === searchSeq) searchResults.value = [];
  } finally {
    if (seq === searchSeq) searching.value = false;
  }
};

watch(query, (q) => {
  if (searchTimer) window.clearTimeout(searchTimer);
  searchSeq += 1;
  if (!q.trim()) {
    searchResults.value = [];
    searching.value = false;
    return;
  }
  searchTimer = window.setTimeout(() => runSearch(q), 300);
});

const openPath = computed(() => driveItemsParentInfo.value?.path || '/');
const isInOpenFolder = (item: DriveItem) => samePath(item.parent_info?.path, openPath.value);

const visibleItems = computed<DriveItem[]>(() => {
  const local = filterDriveItems(driveItems.value ?? [], query.value);
  const all = query.value.trim() ? mergeSearchResults(local, searchResults.value) : local;
  return sortDriveItems(all, sortKey.value, sortDir.value);
});

const searchSummary = computed(() => {
  const n = visibleItems.value.length;
  const where = `${currentFolderName.value} and subfolders`;
  if (searching.value && !n) return `Searching ${where}…`;
  return `${n} ${n === 1 ? 'result' : 'results'} in ${where}`;
});

/** The preview pane shows exactly one selected file (never a folder, never a multi-select). */
const previewItem = computed<DriveItem | null>(() => {
  const only = selectedFiles.value.length === 1 ? selectedFiles.value[0] : null;
  return only && only.type === 'file' ? only : null;
});

/**
 * Previews come from two places. Graph thumbnails cover images, PDF, Office,
 * SVG, AI, PSD, text, video — so every file is asked once. What Graph declines
 * (.cdr, .plt, .eps) is rendered by the API server and held as an object URL.
 * Each item is resolved once per mount: 'pending' while asked, 'none' when
 * neither source has anything, otherwise the URL.
 */
const SERVER_RENDERED = ['cdr', 'plt', 'hpgl', 'hgl', 'eps'];
const thumbState = ref<Record<string, 'pending' | 'none' | string>>({});
const objectUrls: string[] = [];

const resolveThumb = async (item: DriveItem) => {
  try {
    const graph = await getFileThumbnails(item);
    if (graph?.large?.url) {
      thumbState.value[item.id] = graph.large.url;
      return;
    }
    if (SERVER_RENDERED.includes((item.extension ?? '').toLowerCase())) {
      const blob = await getServerPreview(item);
      if (blob) {
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        thumbState.value[item.id] = url;
        return;
      }
    }
    thumbState.value[item.id] = 'none';
  } catch {
    thumbState.value[item.id] = 'none';
  }
};

const thumbUrl = (item: DriveItem): string | null => {
  if (item.type !== 'file') return null;
  const existing = thumbnails.value[item.id];
  if (existing?.large?.url) return existing.large.url;
  const state = thumbState.value[item.id];
  return !state || state === 'pending' || state === 'none' ? null : state;
};

// Ask for previews as items come on screen (never from inside render).
watch(
  visibleItems,
  (items) => {
    items.forEach((item) => {
      if (item.type === 'file' && thumbState.value[item.id] === undefined) {
        thumbState.value[item.id] = 'pending';
        resolveThumb(item);
      }
    });
  },
  { immediate: true }
);

const thumbPending = (item: DriveItem) => thumbState.value[item.id] === 'pending';

const folderSummary = computed(() => folderSummaryText(driveItems.value ?? []));
const footText = computed(() =>
  footTextFor(visibleItems.value.length, driveItems.value?.length ?? 0, selectedFiles.value.length)
);
const currentFolderName = computed(() => {
  const active = driveBreadCrumbs.value?.find((crumb) => crumb.isActive);
  return !active || active.name === '/' ? 'Home' : active.name;
});

const renameFromPreview = (item: DriveItem) => {
  contextMenu.item = item;
  openRenameModal();
};

const deleteFromPreview = (item: DriveItem) => {
  contextMenu.item = item;
  showDeleteConfirmModal();
};

// A new folder (or factory) means a new search and a clean selection.
watch(driveItems, () => {
  query.value = '';
  selectedFiles.value = [];
});

// Selection state
const startX = ref(0);
const startY = ref(0);
const containerRect = ref<DOMRect | null>(null);
const isRightClick = ref(false);
const ctrlKeyPressed = ref(false);
const shiftKeyPressed = ref(false);
const lastClickTime = ref(0);
const clickDelay = ref(300);
const pendingClick = ref<number | null>(null);
const draggedItems = ref<DriveItem[]>([]);
const dragOverItem = ref<DriveItem | null>(null);
const dragCounter = ref(0);
const showConflictModal = ref(false);
const conflictOperation = ref<any | null>(null);
const conflictItems = ref<DriveItem[]>([]);
const conflictTarget = ref<DriveItem | null>(null);
const showUploadCancelConfirm = ref(false);
const preventUnload = ref(false);

// Methods
const handleFactoryUpdate = (updatedFactory: Factory) => {
  activeFactory.value = updatedFactory;
  getSharePointFilesAtPath();
  fetchFactorySharepointInfo();
};

const refreshDirectory = () => {
  contextMenu.visible = false;
  getSharePointFilesAtPath();
};

const getSharePointFilesAtPath = (path = driveItemsParentInfo.value?.path) => {
  if (path) {
    driveItemsParentInfo.value = { ...driveItemsParentInfo.value, path };
    fetchSharePointFiles();
  }
};

const handleItemDoubleClick = (driveItem: DriveItem) => {
  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  if (driveItem.type === 'folder') {
    const driveItemPath =
      driveItem.parent_info.path === '/'
        ? driveItem.name
        : `${driveItem.parent_info.path}/${driveItem.name}`;
    getSharePointFilesAtPath(driveItemPath);
  } else {
    downloadFile(driveItem);
  }
};

const downloadFile = (driveItem: DriveItem) => {
  if (!driveItem.download_url) return;

  const downloadUrl = `${driveItem.download_url}`;
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = driveItem.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleSharePointFilesChangeEvent = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    uploadSharePointFiles(Array.from(target.files));
  }
};

const handleSharePointFilesDropEvent = (event: DragEvent) => {
  if (event.dataTransfer?.files) {
    uploadSharePointFiles(Array.from(event.dataTransfer.files));
  }
  isDragging.value = false;
};

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (isUploading.value) {
    preventUnload.value = true;
    showUploadCancelConfirm.value = true;
    event.preventDefault();
    event.returnValue = '';
  }
};

const handleUploadCancelConfirm = (confirm: boolean) => {
  if (confirm) {
    cancelAllUploads();
    showUploadCancelConfirm.value = false;
    return;
  }
  showUploadCancelConfirm.value = false;
};

const cancelAllUploads = () => {
  Object.keys(uploadProgress.value).forEach((fileName) => {
    cancelUpload(String(fileName));
  });
};

const uploadSharePointFiles = (files: File[]) => {
  isUploading.value = true;

  if (files?.length > 0) {
    isOperationLoading.value = true;

    // Initialize multi-file upload tracking if there are multiple small files
    const smallFiles = files.filter((file) => file.size < 250 * 1024 * 1024);
    if (smallFiles.length > 1) {
      setMultiFileUpload(smallFiles.length);
    }
  }

  files.forEach((file) => {
    const payload = {
      destination_item_id: driveItemsParentInfo.value.id,
      file_name: file.name,
      file: file,
    };

    if (file.size >= 250 * 1024 * 1024) {
      isUploading.value = true;
      uploadLargeSharepointFile(payload)
        .then(handleUploadSuccess)
        .catch(handleUploadError)
        .finally(() => {
          isUploading.value = false;
        });
    } else {
      uploadSharepointFile(payload).then(handleUploadSuccess).catch(handleUploadError);
    }
  });
  isUploading.value = false;
  isOperationLoading.value = false;
};

const handleUploadSuccess = (response: any) => {
  if (response && response.canceled) {
    showToast('Upload cancelled.', 'info');
  } else {
    updateRemainingUploads();
    handleAllFilesUploaded();
  }
};

const handleUploadError = (error: any) => {
  if (axios.isCancel(error)) {
    showToast('Upload cancelled.', 'info');
  } else {
    updateRemainingUploads();
    handleResponseException(error);
  }
};

const updateRemainingUploads = () => {
  // Implementation of updateRemainingUploads method
};

const handleAllFilesUploaded = () => {
  if (multiFileUpload.value.completed >= multiFileUpload.value.total) {
    isOperationLoading.value = false;

    const failedCount = multiFileUpload.value.total - multiFileUpload.value.completed;
    if (failedCount > 0) {
      showToast(`${failedCount} file(s) couldn't be uploaded. Try them again.`, 'error');
    }

    if (multiFileUpload.value.completed > 0) {
      showToast(`${multiFileUpload.value.completed} file(s) uploaded.`, 'success');
    }

    // Add a delay before resetting the multi-file upload state
    setTimeout(() => {
      resetMultiFileUpload();
      refreshDirectory();
    }, 2000);
  }
};

const openCreateFolderModal = () => {
  showCreateFolderModal.value = true;
};

const openContextMenu = (item: DriveItem, event: MouseEvent) => {
  contextMenu.visible = true;
  contextMenu.style = {
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
  };
  contextMenu.item = selectedFiles.value.length > 0 ? selectedFiles.value : item;

  event.stopPropagation();

  nextTick(() => {
    if (contextMenuRef.value) {
      contextMenuRef.value.focus();
    }
  });
};

const closeContextMenu = (event?: Event) => {
  if (!event || !event.target || !(event.target as HTMLElement).closest('.of-menu')) {
    contextMenu.visible = false;
  }
};

const renameItem = () => {
  if (!driveItemNewName.value || !driveItemNewName.value.trim()) {
    showToast('Enter a file name.', 'error');
    return;
  }

  driveItemNewName.value = driveItemNewName.value.trim();
  handleDriveItemAction('rename');
};

const openRenameModal = () => {
  const driveItem = contextMenu.item as DriveItem;
  if (!driveItem) return;

  let driveItemName = driveItem.name;
  let extension = '';

  if (driveItem.type === 'file') {
    extension = driveItemName.split('.').pop() || '';
    driveItemName = driveItemName.slice(0, -(extension?.length + 1));
  }

  driveItemNewName.value = driveItemName;
  contextMenu.visible = false;
  showRenameModal.value = true;
};

const showDeleteConfirmModal = () => {
  itemActionInfo.value = {
    drive_item: contextMenu.item as DriveItem | DriveItem[],
    action: 'delete',
  };
  showDeleteConfirm.value = true;
  contextMenu.visible = false;
};

const toggleItemSelection = (driveItem: DriveItem) => {
  const index = selectedFiles.value.findIndex((item) => item.name === driveItem.name);
  if (index === -1) {
    selectedFiles.value.push(driveItem);
  } else {
    selectedFiles.value.splice(index, 1);
  }
};

const selectRange = (event: MouseEvent, driveItem: DriveItem) => {
  if (event.shiftKey && selectedFiles.value.length) {
    let lastSelected = selectedFiles.value[selectedFiles.value.length - 1];
    let startIndex = driveItems.value.findIndex((item) => item.name === lastSelected.name);
    let endIndex = driveItems.value.findIndex((item) => item.name === driveItem.name);

    let range = driveItems.value.slice(
      Math.min(startIndex, endIndex),
      Math.max(startIndex, endIndex) + 1
    );

    selectedFiles.value = range;
  } else {
    toggleItemSelection(driveItem);
  }
};

const handleContextMenu = (event: MouseEvent, item: DriveItem) => {
  if (selectedFiles.value.length === 0 || !selectedFiles.value.includes(item)) {
    selectedFiles.value = [item];
  }
  contextMenu.visible = true;
  contextMenu.style = {
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
  };
  contextMenu.item = selectedFiles.value.length > 1 ? selectedFiles.value : item;

  nextTick(() => {
    if (contextMenuRef.value) {
      contextMenuRef.value.focus();
    }
  });

  event.stopPropagation();
};

const handleContainerContextMenu = (event: MouseEvent) => {
  if (!(event.target as HTMLElement).closest('.file-item')) {
    contextMenu.visible = true;
    contextMenu.style = {
      top: `${event.clientY}px`,
      left: `${event.clientX}px`,
    };
    contextMenu.item = null;

    nextTick(() => {
      if (contextMenuRef.value) {
        contextMenuRef.value.focus();
      }
    });
  }
};

const startSelection = (event: MouseEvent) => {
  if (!fileContainer.value) return;

  containerRect.value = fileContainer.value.getBoundingClientRect();

  isSelecting.value = true;
  startX.value = event.clientX - containerRect.value.left;
  startY.value = event.clientY - containerRect.value.top;

  selectionBox.left = startX.value;
  selectionBox.top = startY.value;
  selectionBox.width = 0;
  selectionBox.height = 0;

  if (!ctrlKeyPressed.value && !shiftKeyPressed.value) {
    selectedFiles.value = [];
  }
};

const checkSelection = () => {
  if (!fileContainer.value || !driveItems.value.length || !containerRect.value) return;

  const fileElements = new Map<string, Element>();
  fileContainer.value.querySelectorAll<HTMLElement>('.file-item[data-item-id]').forEach((el) => {
    fileElements.set(el.dataset.itemId as string, el);
  });

  const newlySelectedFiles = driveItems.value.filter((file) => {
    const el = fileElements.get(String(file.id));
    if (!el) return false;

    const rect = el.getBoundingClientRect();

    const fileLeft = rect.left - containerRect.value!.left;
    const fileTop = rect.top - containerRect.value!.top;

    return (
      fileLeft < selectionBox.left + selectionBox.width &&
      fileLeft + rect.width > selectionBox.left &&
      fileTop < selectionBox.top + selectionBox.height &&
      fileTop + rect.height > selectionBox.top
    );
  });

  if (ctrlKeyPressed.value) {
    newlySelectedFiles.forEach((file) => {
      const index = selectedFiles.value.findIndex((f) => f.id === file.id);
      if (index === -1) {
        selectedFiles.value.push(file);
      } else {
        selectedFiles.value.splice(index, 1);
      }
    });
  } else {
    selectedFiles.value = ctrlKeyPressed.value
      ? [
          ...selectedFiles.value,
          ...newlySelectedFiles.filter((file) => !selectedFiles.value.includes(file)),
        ]
      : newlySelectedFiles;
  }
};

const updateSelection = (event: MouseEvent) => {
  if (!isSelecting.value || !containerRect.value) return;

  const currentX = event.clientX - containerRect.value.left;
  const currentY = event.clientY - containerRect.value.top;

  selectionBox.left = Math.min(startX.value, currentX);
  selectionBox.top = Math.min(startY.value, currentY);
  selectionBox.width = Math.abs(startX.value - currentX);
  selectionBox.height = Math.abs(startY.value - currentY);

  checkSelection();
};

const handleMouseDown = (event: MouseEvent) => {
  if (event.button === 2) {
    isRightClick.value = true;
    return;
  }

  isRightClick.value = false;

  if (
    !(event.target as HTMLElement).closest('.file-item') ||
    ctrlKeyPressed.value ||
    shiftKeyPressed.value
  ) {
    startSelection(event);
  }
};

const handleItemClick = (event: MouseEvent, item: DriveItem) => {
  if (isSelecting.value) return;

  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  const clickTime = new Date().getTime();
  const isDoubleClick = clickTime - lastClickTime.value < clickDelay.value;
  lastClickTime.value = clickTime;

  if (isDoubleClick) {
    return;
  }

  pendingClick.value = window.setTimeout(() => {
    showFileLogs(item);
    if (ctrlKeyPressed.value) {
      const index = selectedFiles.value.indexOf(item);
      if (index === -1) {
        selectedFiles.value.push(item);
      } else {
        selectedFiles.value.splice(index, 1);
      }
      event.preventDefault();
      return;
    }

    if (shiftKeyPressed.value) {
      // If no items are selected, just select the clicked item
      if (selectedFiles.value.length === 0) {
        selectedFiles.value = [item];
        return;
      }

      const lastSelected = selectedFiles.value[selectedFiles.value.length - 1];
      const lastIndex = driveItems.value.findIndex((di) => di.id === lastSelected.id);
      const currentIndex = driveItems.value.findIndex((di) => di.id === item.id);

      // If either index is not found, just select the clicked item
      if (lastIndex === -1 || currentIndex === -1) {
        selectedFiles.value = [item];
        return;
      }

      const start = Math.min(lastIndex, currentIndex);
      const end = Math.max(lastIndex, currentIndex);

      selectedFiles.value = driveItems.value.slice(start, end + 1);
      event.preventDefault();
      return;
    }

    if (!ctrlKeyPressed.value && !shiftKeyPressed.value) {
      selectedFiles.value = [item];
    }
  }, clickDelay.value);
};

const endSelection = () => {
  isSelecting.value = false;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlKeyPressed.value = true;
  } else if (event.key === 'Shift') {
    shiftKeyPressed.value = true;
  }
};

const handleKeyUp = (event: KeyboardEvent) => {
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlKeyPressed.value = false;
  } else if (event.key === 'Shift') {
    shiftKeyPressed.value = false;
  }
};

const handleContainerDragOver = (event: DragEvent) => {
  if (!isMovingItems.value) {
    isDragging.value = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }
};

const handleContainerDragLeave = (event: DragEvent) => {
  if (!isMovingItems.value && !(event.relatedTarget as HTMLElement)?.closest('.of-card')) {
    isDragging.value = false;
  }
};

const handleContainerDrop = (event: DragEvent) => {
  if (!isMovingItems.value) {
    handleSharePointFilesDropEvent(event);
  }
  isDragging.value = false;
  isMovingItems.value = false;
};

const handleDragLeave = (event: DragEvent, item: DriveItem) => {
  if (item && dragOverItem.value === item) {
    dragOverItem.value = null;
  }
};

const handleDragStart = (event: DragEvent, item: DriveItem) => {
  isMovingItems.value = true;
  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  if (!selectedFiles.value.includes(item)) {
    selectedFiles.value = [item];
  }

  draggedItems.value = [...selectedFiles.value];

  if (selectedFiles.value.length > 1 && event.dataTransfer) {
    const dragImage = document.createElement('div');
    dragImage.className = 'drag-image';
    const dragCount = document.createElement('div');
    dragCount.className = 'drag-count';
    dragCount.textContent = `${selectedFiles.value.length} items`;
    dragImage.appendChild(dragCount);
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 25, 25);
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  }

  if (event.dataTransfer) {
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        action: 'move',
        items: draggedItems.value.map((item) => item.id),
      })
    );

    event.dataTransfer.effectAllowed = 'move';
  }
};

const handleDragOver = (event: DragEvent, item: DriveItem) => {
  if (item.type === 'folder' && event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
    dragOverItem.value = item;
  } else if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none';
  }
};

const handleDrop = (event: DragEvent, targetFolder: DriveItem) => {
  if (targetFolder.type !== 'folder') {
    return;
  }

  dragOverItem.value = null;

  try {
    if (event.dataTransfer) {
      const data = JSON.parse(event.dataTransfer.getData('text/plain'));

      if (data.action === 'move' && data.items && data.items.length > 0) {
        const itemsToMove = draggedItems.value.filter(
          (item) =>
            !(
              item.id === targetFolder.id ||
              (item.type === 'folder' && targetFolder.parent_info.path.includes(item.name))
            )
        );

        if (itemsToMove.length === 0) {
          showToast('A folder can\'t be moved into itself.', 'error');
          return;
        }

        executeMoveOperation(itemsToMove, targetFolder, false);
      }
    }
  } catch (error) {
    console.error('Error processing drop:', error);
  }
};

const handleDragEnd = () => {
  draggedItems.value = [];
  dragOverItem.value = null;
  isMovingItems.value = false;
};

const executeMoveOperation = async (
  itemsToMove: DriveItem[],
  targetFolder: DriveItem,
  forceRename = false
) => {
  if (moveOperationInProgress.value) {
    return;
  }

  moveOperationInProgress.value = true;
  isOperationLoading.value = true;

  try {
    const movePromises = itemsToMove.map((item) => {
      return moveSharepointItem(item.id, targetFolder.id, forceRename);
    });

    await Promise.all(movePromises);

    showToast(`${itemsToMove.length} item(s) moved.`, 'success');
    refreshDirectory();
    isDragging.value = false;
    moveOperationInProgress.value = false;
    isOperationLoading.value = false;
  } catch (error) {
    isOperationLoading.value = false;
    isDragging.value = false;
    moveOperationInProgress.value = false;

    if (error.response && error.response.status === 409) {
      handleConflict(itemsToMove, targetFolder);
    } else {
      handleResponseException(error);
    }
  }
};

const handleConflict = (itemsToMove: DriveItem[], targetFolder: DriveItem) => {
  showConflictModal.value = true;
  conflictItems.value = itemsToMove;
  conflictTarget.value = targetFolder;
};

const resolveConflict = (shouldRename: boolean) => {
  showConflictModal.value = false;

  if (shouldRename && conflictTarget.value) {
    executeMoveOperation(conflictItems.value, conflictTarget.value, true);
  } else {
    showToast('Move cancelled.', 'info');
  }

  conflictItems.value = [];
  conflictTarget.value = null;
};

const cancelUpload = (fileName: string) => {
  const cancelToken = cancelTokens.value[fileName];
  if (cancelToken) {
    cancelToken.cancel('Upload canceled by user');
    removeCancelToken(fileName);
    clearFileUploadProgress(fileName);
  }
};

const showFileLogs = async (file) => {
  emits('loading-logs');
  let fileLogs = await getFileLogs(file);

  emits('show-logs',{'logs': fileLogs, 'file': file});
  emits('loading-logs');
};

// Helper function for error handling
const handleResponseException = (error: any) => {
  console.error('API Error:', error);
  showToast(messageForError(error), 'error');
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', closeContextMenu);
  document.addEventListener('mouseup', endSelection);
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('beforeunload', handleBeforeUnload);

  fetchSharePointFiles();
  fetchFactorySharepointInfo();
});

onBeforeUnmount(() => {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
  document.removeEventListener('click', closeContextMenu);
  document.removeEventListener('mouseup', endSelection);
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<style lang="scss">
/* AB#21539 — the Files tab. Same vocabulary as the dashboard and ACL pages:
   one card, a single toolbar band, quiet borders, green for the primary action. */
.of {
  @apply w-full;
}
.of-card {
  @apply relative flex flex-col bg-surface border border-line rounded-[10px] overflow-hidden;
  min-height: 360px;
}
.of-card--drop {
  @apply border-[var(--accent)];
  box-shadow: inset 0 0 0 2px var(--accent);
}

/* toolbar */
.of-top {
  @apply flex flex-wrap items-center gap-2 px-3 py-2 border-b border-line;
}
.of-sp {
  @apply flex-1;
}
.of-tabs {
  @apply flex items-center gap-1 flex-wrap;
}
.of-tab {
  @apply flex items-center gap-1.5 text-sm font-medium text-ink-soft px-2.5 h-[32px] rounded-[8px] bg-transparent border-0 transition-colors;
}
.of-tab:hover {
  @apply bg-surface-2 text-ink;
}
.of-tab--on {
  @apply bg-[var(--accent-soft)] text-[var(--accent-strong)];
}
.of-tab:focus-visible {
  @apply outline outline-2 outline-offset-1 outline-[var(--accent)];
}
.of-tab-n {
  @apply text-[11px] font-semibold px-1.5 rounded-full bg-surface text-[var(--accent-strong)] leading-[18px];
  font-variant-numeric: tabular-nums;
}
.of-search {
  @apply flex items-center gap-2 px-3 h-[36px] min-w-[180px] max-w-[320px] flex-1 bg-surface border border-line rounded-[8px] text-ink-soft;
}
.of-search:focus-within {
  @apply border-[var(--accent)];
}
.of-search input {
  @apply flex-1 min-w-0 border-0 bg-transparent text-sm text-ink p-0;
}
.of-search input:focus {
  @apply outline-none;
}
.of-search input::placeholder {
  @apply text-ink-mute;
}
.of-seg {
  @apply inline-flex border border-line rounded-[8px] overflow-hidden bg-surface;
}
.of-seg button {
  @apply flex items-center justify-center w-[36px] h-[34px] text-ink-soft bg-transparent border-0 transition-colors;
}
.of-seg button + button {
  @apply border-l border-line;
}
.of-seg button:hover {
  @apply bg-surface-2 text-ink;
}
.of-seg button:focus-visible {
  @apply outline outline-2 outline-offset-[-2px] outline-[var(--accent)];
}
.of-seg .of-seg--on {
  @apply bg-[var(--accent-soft)] text-[var(--accent-strong)];
}

.of-blocked {
  @apply m-3 px-4 py-3 rounded-[8px] bg-[var(--warning-soft)] text-[#8A4B00] text-sm;
}

/* breadcrumb */
.of-crumb {
  @apply flex flex-wrap items-center gap-1 px-3 h-[40px] text-sm text-ink-soft border-b border-line;
}
.of-crumb-ico {
  @apply text-ink-mute mr-1;
}
.of-crumb-sep {
  @apply text-[#C5C9CF] px-0.5;
}
.of-crumb-a {
  @apply text-ink bg-transparent border-0 px-1 rounded-[4px] hover:bg-surface-2 hover:text-ink;
}
.of-crumb-a:focus-visible {
  @apply outline outline-2 outline-[var(--accent)];
}
.of-crumb-here {
  @apply font-semibold text-ink px-1;
}
.of-crumb-meta {
  @apply ml-2 text-ink-mute text-xs;
}
.of-paste {
  @apply ml-auto flex items-center gap-1.5 text-xs font-medium text-[var(--accent-strong)] bg-[var(--accent-soft)] px-2.5 h-[28px] rounded-[6px] border-0;
}
.of-paste:hover {
  @apply bg-[var(--accent-soft-2)];
}

/* body + preview */
.of-split {
  @apply flex flex-1 min-h-0;
}
.of-body {
  @apply relative flex-1 min-w-0 overflow-auto select-none;
  min-height: 260px;
}
.of-loading {
  @apply absolute inset-0 z-10 flex items-center justify-center bg-surface;
}
.of-spinner {
  @apply block w-7 h-7 rounded-full border-[3px] border-line border-t-[var(--accent)];
  animation: of-spin 0.8s linear infinite;
}
@keyframes of-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .of-spinner {
    animation-duration: 2s;
  }
}

/* list */
.of-table {
  @apply w-full text-sm border-collapse;
}
.of-table th {
  @apply text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-soft px-3 h-[36px] border-b border-line bg-surface-2 whitespace-nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}
.of-table td {
  @apply px-3 h-[44px] border-b border-[#F2F3F5] align-middle text-ink;
}
.of-sort {
  @apply inline-flex items-center gap-1 bg-transparent border-0 p-0 text-inherit uppercase cursor-pointer;
  font: inherit;
}
.of-sort[aria-sort='ascending'],
.of-sort[aria-sort='descending'] {
  @apply text-[var(--accent-strong)];
}
.of-sort:focus-visible {
  @apply outline outline-2 outline-[var(--accent)] rounded-[3px];
}
.of-col-n {
  @apply w-[110px] whitespace-nowrap;
}
.of-col-d {
  @apply w-[130px] whitespace-nowrap;
}
.of-col-by {
  @apply w-[150px] whitespace-nowrap;
}
.of-col-x {
  @apply w-[44px] text-right;
}
.of-mono {
  font-variant-numeric: tabular-nums;
}
.of-muted {
  @apply text-ink-mute;
}
.of-row {
  @apply cursor-default transition-colors;
}
.of-row:hover td {
  @apply bg-surface-2;
}
.of-row--sel td {
  @apply bg-[var(--accent-soft)];
}
.of-row--over td {
  box-shadow: inset 0 0 0 2px var(--accent);
}
.of-name {
  @apply flex items-center gap-2 min-w-0;
}
.of-name-t {
  @apply truncate font-medium;
}
.of-ext {
  @apply text-ink-mute text-xs;
}
.of-ico-folder {
  @apply text-[#E2A02B] shrink-0;
}
.of-ico-file {
  @apply text-ink-mute shrink-0;
}
.of-ico-thumb {
  @apply w-[22px] h-[22px] rounded-[4px] object-cover border border-line shrink-0;
}
.of-by {
  @apply inline-flex items-center gap-1.5 text-ink;
}
.of-av {
  @apply inline-flex items-center justify-center w-[22px] h-[22px] rounded-full text-white text-[10px] font-bold;
}
.of-kebab {
  @apply inline-flex items-center justify-center w-[30px] h-[30px] rounded-[6px] text-ink-soft bg-transparent border-0 opacity-0 transition-opacity;
}
.of-row:hover .of-kebab,
.of-row--sel .of-kebab,
.of-tile:hover .of-kebab,
.of-tile--sel .of-kebab,
.of-kebab:focus-visible {
  @apply opacity-100;
}
.of-kebab:hover {
  @apply bg-[#E4E6EA] text-ink;
}
.of-kebab:focus-visible {
  @apply outline outline-2 outline-[var(--accent)];
}
.of-empty-row td,
.of-empty {
  @apply text-center text-ink-mute text-sm py-10;
}

/* where a search result lives */
.of-where {
  @apply ml-1 text-xs text-[var(--accent-strong)] bg-[var(--accent-soft)] px-1.5 rounded-[4px] border-0 whitespace-nowrap max-w-[220px] truncate;
}
.of-where:hover {
  @apply bg-[var(--accent-soft-2)];
}
.of-tile-where {
  @apply text-xs text-[var(--accent-strong)] truncate;
}

/* grid */
.of-grid {
  @apply grid gap-3 p-3;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
}
.of-grid .of-empty {
  grid-column: 1 / -1;
}
.of-tile {
  @apply relative flex flex-col gap-1.5 p-2 rounded-[10px] border border-transparent cursor-default transition-colors;
}
.of-tile:hover {
  @apply bg-surface-2 border-line;
}
.of-tile--sel {
  @apply bg-[var(--accent-soft)] border-[var(--accent-soft-2)];
}
.of-tile--over {
  box-shadow: inset 0 0 0 2px var(--accent);
}
.of-thumb {
  @apply flex items-center justify-center h-[96px] rounded-[8px] bg-surface-2 overflow-hidden;
}
.of-thumb-img {
  @apply w-full h-full object-cover;
}
.of-tile-t {
  @apply text-sm font-medium text-[#1C1C1F] truncate;
}
.of-tile-s {
  @apply flex justify-between gap-2 text-xs text-ink-mute;
  font-variant-numeric: tabular-nums;
}
.of-tile-kebab {
  @apply absolute top-2 right-2 bg-surface shadow-sm;
}

.of-marquee {
  @apply absolute z-20 pointer-events-none border border-[var(--accent)] bg-primary/10;
}

/* preview */
.of-preview {
  @apply relative w-[280px] shrink-0 border-l border-line p-4 flex flex-col gap-3 bg-surface-2 overflow-auto;
}
.of-preview-x {
  @apply absolute top-2 right-2 z-[1] bg-surface shadow-sm;
}
.of-preview-big {
  @apply flex items-center justify-center h-[160px] rounded-[8px] bg-surface border border-line overflow-hidden;
}
.of-preview-big img {
  @apply max-w-full max-h-full object-contain;
}
.of-preview-big--none {
  @apply flex-col gap-2 bg-surface-2;
}
.of-ext-badge {
  @apply inline-flex items-center justify-center px-2 h-[28px] rounded-[6px] bg-[#EEF0F2] text-ink-soft text-xs font-bold tracking-[0.06em];
}
.of-preview-note {
  @apply text-xs text-ink-mute;
}
.of-preview-t {
  @apply text-sm font-semibold text-ink break-words m-0 pr-8;
}
.of-preview-dl {
  @apply grid gap-x-3 gap-y-1.5 text-xs m-0;
  grid-template-columns: 64px 1fr;
}
.of-preview-dl dt {
  @apply text-ink-mute uppercase tracking-[0.04em] text-[10px] leading-[18px];
}
.of-preview-dl dd {
  @apply m-0 text-ink break-words;
}
.of-preview-acts {
  @apply flex flex-wrap gap-2 mt-auto;
}
.of-danger {
  @apply text-[#B42318];
}
.of-danger:hover {
  @apply bg-[#FEF3F2] border-[#F0B8B3];
}

/* footer + cues */
.of-foot {
  @apply flex flex-wrap justify-between gap-2 px-3 min-h-[36px] items-center text-xs text-ink-soft border-t border-line bg-surface-2;
}
.of-dropcue {
  @apply absolute inset-2 z-30 flex items-center justify-center rounded-[8px] border-2 border-dashed border-[var(--accent)] bg-primary-light text-[var(--accent-strong)] font-medium pointer-events-none;
}

/* context menu (⋯ and right-click) */
.of-menu {
  @apply fixed z-[10030] flex flex-col p-1.5 min-w-[180px] bg-surface border border-line rounded-[8px] shadow-lg outline-none;
}
.of-menu-i {
  @apply w-full text-left text-sm text-ink px-3 py-2 rounded-[6px] whitespace-nowrap bg-transparent border-0 transition-colors;
}
.of-menu-i:hover,
.of-menu-i:focus-visible {
  @apply bg-surface-2 outline-none;
}
.of-menu-i--danger {
  @apply text-[#B42318];
}
.of-menu-i--danger:hover {
  @apply bg-[#FEF3F2];
}
.of-menu-hr {
  @apply my-1 border-0 border-t border-line;
}

@media (max-width: 767px) {
  .of-split--preview {
    @apply flex-col;
  }
  .of-preview {
    @apply w-full border-l-0 border-t border-line;
  }
  .of-col-d,
  .of-col-by {
    @apply hidden;
  }
  .of-kebab {
    @apply opacity-100;
  }
  .of-foot .of-muted {
    @apply hidden;
  }
}

/* kept from before: the drag ghost for multi-item moves */
.drag-image {
  position: fixed;
  top: -1000px;
  left: -1000px;
  z-index: -1;
  @apply bg-primary/80 rounded-full w-[50px] h-[50px] flex items-center justify-center;
}
.drag-count {
  @apply text-white font-bold text-center;
}
</style>
